import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    TextField,
    IconButton,
    Menu,
    MenuItem,
    Button,
    InputAdornment,
    Table,
    Select,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    RadioGroup,
    Radio,
    FormControlLabel,
    FormLabel,
    Alert,
} from "@mui/material";
import { Row, Col } from "react-bootstrap";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { getData, postData, API_ENDPOINTS } from "../../services/apiService";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LoadingSpinner from "../LoadingContext";
import { encryptId } from "../helper/idCodec.js";
import { useNavigate } from "react-router-dom";
import "./Packages.css";

function Packages() {
    const [searchTextValue, setSearchTextValue] = useState("");
    const [filterMenuAnchorEl, setFilterMenuAnchorEl] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null);
    const [packages, setPackages] = useState([]);
    const [totalPackages, setTotalPackages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(0);
    const [packagesPerPage, setPackagesPerPage] = useState(10);
    const openFilterMenu = Boolean(filterMenuAnchorEl);
    const handleMoreOptionsClick = (event, packageId) => { setUserMenuAnchorEl(event.currentTarget); setSelectedPackageId(packageId); };
    const handleUserMenuClose = () => { setUserMenuAnchorEl(null); setSelectedPackageId(null); };
    const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
    const openUserMenu = Boolean(userMenuAnchorEl);
    const [selectedPackageId, setSelectedPackageId] = useState(null);
    const navigate = useNavigate();

    const fetchPackages = async (search = "", status = null, page = 0, limit = 10) => {
        const startTime = Date.now(); // Record when loading starts
        try {
            setLoading(true);
            const params = {
                search: search || null,
                status: status || null,
                page: page + 1,
                limit,
                from_date: null,
                to_date: null,
            };

            const res = await getData(API_ENDPOINTS.fetchAllPackages, params);
            if (res.statusCode === 200) {
                setPackages(res.result.packages || []);
                setTotalPackages(res.result.totalPackages || 0);
            } else {
                setPackages([]);
                setTotalPackages(0);
            }
        } catch (error) {
            console.error("Error fetching packages:", error);
            setPackages([]);
            setTotalPackages(0);
        } finally {
            // Ensure at least 3 seconds of loading time
            const elapsed = Date.now() - startTime;
            const remaining = Math.max(0, 3000 - elapsed);
            setTimeout(() => setLoading(false), remaining);
        }
    };

    // ---------------- Handlers ----------------
    const handleSearchTextChange = (e) => setSearchTextValue(e.target.value);

    const handleSearchKeyPress = (e) => {
        if (e.key === "Enter") {
            setCurrentPage(0);
            fetchPackages(searchTextValue, statusFilter, 0, packagesPerPage);
        }
    };

    const handleClearSearch = () => {
        setSearchTextValue("");
        setCurrentPage(0);
        fetchPackages("", statusFilter, 0, packagesPerPage);
    };

    const handleFilterButtonClick = (e) => setFilterMenuAnchorEl(e.currentTarget);
    const handleFilterMenuClose = () => setFilterMenuAnchorEl(null);

    const handleStatusFilterChange = (status) => {
        const newStatus = status === "all" ? null : status;
        setStatusFilter(newStatus);
        setCurrentPage(0);
        fetchPackages(searchTextValue, newStatus, 0, packagesPerPage);
        setFilterMenuAnchorEl(null);
    };

    const handlePackagesPerPageChange = (e) => {
        const newLimit = Number(e.target.value);
        setPackagesPerPage(newLimit);
        setCurrentPage(0);
        fetchPackages(searchTextValue, statusFilter, 0, newLimit);
    };

    const handlePageChange = (event, newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
            fetchPackages(searchTextValue, statusFilter, newPage, packagesPerPage);
        }
    };

    // ---------------- Derived Pagination Data ----------------
    const totalPages = Math.ceil(totalPackages / packagesPerPage);

    // ---------------- Initial Fetch ----------------
    useEffect(() => {
        fetchPackages();
    }, []);

    const handleViewProfile = useCallback((packageId) => {
        navigate(`/app/package-info/${encryptId(packageId)}`);
        handleUserMenuClose();
    }, [navigate]);

    // ─── Cancel package dialog ──────────────────────────────────────────
    // The trip-end gate (`trip.controller::endTrip`) refuses to close a
    // trip while any package is still in {Requested, Accepted, Picked}.
    // This dialog is the admin's escape hatch — it archives the mapping(s)
    // and decides what to do with the customer's payment.
    const IN_FLIGHT_STATUSES = ["Requested", "Accepted", "Picked"];
    const isInFlight = (status) =>
        IN_FLIGHT_STATUSES.includes((status || "").toString());

    const [cancelDlg, setCancelDlg] = useState({
        open: false,
        pkg: null,
        refundDecision: "refund",
        reason: "",
        submitting: false,
        error: "",
    });

    const handleOpenCancel = (pkg) => {
        setCancelDlg({
            open: true,
            pkg,
            refundDecision: "refund",
            reason: "",
            submitting: false,
            error: "",
        });
        handleUserMenuClose();
    };

    const handleCloseCancel = () => {
        if (cancelDlg.submitting) return;
        setCancelDlg((d) => ({ ...d, open: false }));
    };

    const submitCancel = async () => {
        if (!cancelDlg.pkg) return;
        setCancelDlg((d) => ({ ...d, submitting: true, error: "" }));
        try {
            const res = await postData(API_ENDPOINTS.cancelPackageMappingByAdmin, {
                package_id: cancelDlg.pkg.package_id,
                refund_decision: cancelDlg.refundDecision,
                reason: cancelDlg.reason || null,
            });
            if (res?.statusCode === 200) {
                setCancelDlg({
                    open: false,
                    pkg: null,
                    refundDecision: "refund",
                    reason: "",
                    submitting: false,
                    error: "",
                });
                fetchPackages(searchTextValue, statusFilter, currentPage, packagesPerPage);
            } else {
                setCancelDlg((d) => ({
                    ...d,
                    submitting: false,
                    error: res?.message || "Cancel failed.",
                }));
            }
        } catch (e) {
            const msg =
                e?.response?.data?.message || e?.message || "Cancel failed.";
            setCancelDlg((d) => ({ ...d, submitting: false, error: msg }));
        }
    };

    return (
        <>
            <LoadingSpinner isLoading={loading} />
            <Box className="packages-container">
                <Typography variant="h5" component="h1" gutterBottom className="main-title">
                    Packages
                </Typography>

                <Box>
                    <Row className="control-bar mb-4">
                        <Col xs={12} md={7} lg={8} className="search-col">
                            <TextField
                                fullWidth
                                placeholder="Search packages using name, type, or mobile number"
                                variant="outlined"
                                size="small"
                                value={searchTextValue}
                                onChange={handleSearchTextChange}
                                onKeyDown={handleSearchKeyPress}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon color="action" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchTextValue && (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={handleClearSearch}>
                                                <ClearIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Col>

                        <Col
                            xs={12}
                            md={5}
                            lg={4}
                            className="d-flex justify-content-end align-items-center mt-2 mt-md-0"
                        >
                            <Button
                                variant="outlined"
                                startIcon={<FilterListIcon />}
                                onClick={handleFilterButtonClick}
                                aria-controls={openFilterMenu ? "filter-menu" : undefined}
                                aria-haspopup="true"
                                aria-expanded={openFilterMenu ? "true" : undefined}
                                className="filter-button"
                            >
                                Filter
                            </Button>

                            <Menu
                                anchorEl={filterMenuAnchorEl}
                                open={openFilterMenu}
                                onClose={handleFilterMenuClose}
                            >
                                <MenuItem onClick={() => handleStatusFilterChange("all")}>All</MenuItem>
                                <MenuItem onClick={() => handleStatusFilterChange("active")}>Unassigned</MenuItem>
                                <MenuItem onClick={() => handleStatusFilterChange("inactive")}>Inactive</MenuItem>
                                <MenuItem onClick={() => handleStatusFilterChange("on-trip")}>On-Trip</MenuItem>
                                <MenuItem onClick={() => handleStatusFilterChange("maintenance")}>Maintenance</MenuItem>
                            </Menu>
                        </Col>
                    </Row>

                    {!loading && (
                        <div className="list-view-container">
                            <Table responsive hover className="user-list-table">
                                <thead>
                                    <tr>
                                        <th>Tracking Code</th>
                                        <th>Package</th>
                                        <th>Customer Name</th>
                                        <th>Mobile Number</th>
                                        <th>Status</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {packages.length > 0 ? (
                                        packages.map((pkg) => (
                                            <tr key={pkg.package_id}>
                                                <td style={{ fontFamily: "monospace", fontSize: "12px", fontWeight: 600 }}>{pkg.tracking_code || "--"}</td>
                                                <td>{pkg.package_name || "N/A"}</td>
                                                <td>{pkg.package_sending_person || "N/A"}</td>
                                                <td>{pkg.sending_person_mobile_no || "N/A"}</td>
                                                <td>{pkg.status || "N/A"}</td>
                                                <td className="text-end">
                                                    <IconButton size="small" onClick={(e) => handleMoreOptionsClick(e, pkg.package_id)}>
                                                        <MoreVertIcon />
                                                    </IconButton>
                                                    <Menu
                                                        anchorEl={userMenuAnchorEl}
                                                        open={openUserMenu && selectedPackageId === pkg.package_id}
                                                        onClose={handleUserMenuClose}
                                                        PaperProps={{ sx: { boxShadow: "none", border: "1px solid #ddd" } }}
                                                    >
                                                        <MenuItem onClick={() => handleViewProfile(selectedPackageId)}>View</MenuItem>
                                                        {isInFlight(pkg.status) && (
                                                            <MenuItem
                                                                onClick={() => handleOpenCancel(pkg)}
                                                                sx={{ color: "error.main" }}
                                                            >
                                                                Cancel package
                                                            </MenuItem>
                                                        )}
                                                    </Menu>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="text-center text-muted">
                                                No packages found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    {!loading && totalPackages > 0 && (
                        <Box
                            className="pagination-controls"
                            display="flex"
                            justifyContent="end"
                            alignItems="center"
                            mt={2}
                        >
                            <div className="items-per-page" style={{ display: "flex", alignItems: "center" }}>
                                <Select
                                    value={packagesPerPage}
                                    onChange={handlePackagesPerPageChange}
                                    size="small"
                                >
                                    {[5, 10, 25, 50].map((opt) => (
                                        <MenuItem key={opt} value={opt}>
                                            {opt}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <Typography variant="body2" sx={{ ml: 1 }}>
                                    per page
                                </Typography>
                            </div>

                            <Typography variant="body2" sx={{ mx: 2 }}>
                                Page {currentPage + 1} of {totalPages || 1}
                            </Typography>

                            <IconButton
                                onClick={(e) => handlePageChange(e, currentPage - 1)}
                                disabled={currentPage === 0}
                            >
                                <KeyboardArrowLeft />
                            </IconButton>
                            <IconButton
                                onClick={(e) => handlePageChange(e, currentPage + 1)}
                                disabled={currentPage >= totalPages - 1}
                            >
                                <KeyboardArrowRight />
                            </IconButton>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Cancel package dialog — admin escape hatch for the
                trip-end gate. See `cancelPackageMappingByAdmin` on the
                backend for the contract. */}
            <Dialog open={cancelDlg.open} onClose={handleCloseCancel} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 600 }}>
                    Cancel package
                    {cancelDlg.pkg ? ` — ${cancelDlg.pkg.package_name || `#${cancelDlg.pkg.package_id}`}` : ""}
                </DialogTitle>
                <DialogContent dividers>
                    {cancelDlg.error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {cancelDlg.error}
                        </Alert>
                    )}
                    <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
                        This will archive the package on the trip the driver is on, so
                        the trip can be ended. It does not affect already-delivered
                        packages on the same trip.
                    </Typography>

                    <FormLabel sx={{ display: "block", mb: 1, fontWeight: 600 }}>
                        Customer payment
                    </FormLabel>
                    <RadioGroup
                        value={cancelDlg.refundDecision}
                        onChange={(e) =>
                            setCancelDlg((d) => ({ ...d, refundDecision: e.target.value }))
                        }
                    >
                        <FormControlLabel
                            value="refund"
                            control={<Radio />}
                            label="Refund customer (Payment status → refunded)"
                        />
                        <FormControlLabel
                            value="keep"
                            control={<Radio />}
                            label="Platform keeps payment (no refund)"
                        />
                    </RadioGroup>

                    <TextField
                        label="Reason (optional, for audit log)"
                        value={cancelDlg.reason}
                        onChange={(e) =>
                            setCancelDlg((d) => ({ ...d, reason: e.target.value }))
                        }
                        fullWidth
                        multiline
                        minRows={2}
                        sx={{ mt: 2 }}
                        placeholder="e.g. Customer unreachable; goods returned to origin."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCancel} disabled={cancelDlg.submitting}>
                        Close
                    </Button>
                    <Button
                        onClick={submitCancel}
                        variant="contained"
                        color="error"
                        disabled={cancelDlg.submitting}
                    >
                        {cancelDlg.submitting ? "Cancelling…" : "Confirm cancel"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default Packages;
